import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  getStoredGa4EditToken,
  isGa4EditTokenValid,
  clearStoredGa4EditToken,
  requestGa4EditToken,
  syncGa4EditTokenFromSession,
  loadEditToken,
  saveEditToken,
} from "../utils/ga4Tokens"

const DEFAULT_POLL_INTERVAL_MS = 3000
const DEFAULT_MAX_POLL_ATTEMPTS = 5
const SIGN_IN_REQUIRED_MESSAGE =
  "You must be signed in to authorize GA4 edits."
const POLL_FAILURE_MESSAGE =
  "We couldn't confirm GA4 edit access. Please retry the authorization."
const DENIED_MESSAGE =
  "Google denied GA4 edit access. Please authorize the edit scope and try again."

const getInitialTokenRecord = (user) => {
  if (!user?.id) return null
  return getStoredGa4EditToken(user.id)
}

export default function useGa4EditAuthorization({
  supabaseClient,
  user,
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
  maxPollAttempts = DEFAULT_MAX_POLL_ATTEMPTS,
} = {}) {
  const [editTokenRecord, setEditTokenRecord] = useState(() =>
    getInitialTokenRecord(user)
  )
  const [editAuthPending, setEditAuthPending] = useState(false)
  const [editAuthMessage, setEditAuthMessage] = useState(null)
  const [editAuthError, setEditAuthError] = useState(null)
  const [editAuthUrl, setEditAuthUrl] = useState(null)
  const [editPopupBlocked, setEditPopupBlocked] = useState(false)
  const timerRef = useRef(null)
  const prevUserIdRef = useRef(user?.id || null)

  const clearPollTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  useEffect(() => () => clearPollTimer(), [clearPollTimer])

  const updateEditTokenRecord = useCallback(
    (record) => {
      setEditTokenRecord(record)
      if (record?.token && isGa4EditTokenValid(record)) {
        clearPollTimer()
        setEditAuthPending(false)
        setEditAuthMessage(null)
        setEditAuthError(null)
        setEditAuthUrl(null)
        setEditPopupBlocked(false)
      }
    },
    [clearPollTimer]
  )

  const resetAuthorizationState = useCallback(() => {
    clearPollTimer()
    setEditAuthPending(false)
    setEditAuthMessage(null)
    setEditAuthError(null)
    setEditAuthUrl(null)
    setEditPopupBlocked(false)
  }, [clearPollTimer])

  const refreshTokenFromStorage = useCallback(() => {
    if (!user?.id) return null
    const stored = getStoredGa4EditToken(user.id)
    if (stored && isGa4EditTokenValid(stored)) {
      updateEditTokenRecord(stored)
    }
    return stored
  }, [updateEditTokenRecord, user?.id])

  useEffect(() => {
    const currentId = user?.id || null
    if (prevUserIdRef.current === currentId) return
    if (!currentId) {
      if (prevUserIdRef.current) {
        saveEditToken(supabaseClient, prevUserIdRef.current, {
          access_token: null,
        }).catch(() => {})
      }
      clearStoredGa4EditToken()
      updateEditTokenRecord(null)
      resetAuthorizationState()
    } else {
      const stored = getStoredGa4EditToken(currentId)
      updateEditTokenRecord(stored)
      if (!stored) {
        setEditAuthMessage(null)
        setEditAuthError(null)
      }
    }
    prevUserIdRef.current = currentId
  }, [resetAuthorizationState, supabaseClient, updateEditTokenRecord, user?.id])

  useEffect(() => {
    if (!user?.id) return
    if (isGa4EditTokenValid(editTokenRecord)) return
    let cancelled = false
    const hydrateFromStorage = async () => {
      const loaded = await loadEditToken(supabaseClient, user.id)
      if (
        !cancelled &&
        loaded?.token &&
        isGa4EditTokenValid(loaded)
      ) {
        updateEditTokenRecord(loaded)
      }
    }
    hydrateFromStorage()
    return () => {
      cancelled = true
    }
  }, [editTokenRecord, supabaseClient, updateEditTokenRecord, user?.id])

  useEffect(() => {
    if (!user?.id) return
    if (isGa4EditTokenValid(editTokenRecord)) return
    let cancelled = false
    const hydrate = async () => {
      const synced = await syncGa4EditTokenFromSession(
        supabaseClient,
        user.id
      )
      if (
        !cancelled &&
        synced?.token &&
        isGa4EditTokenValid(synced)
      ) {
        updateEditTokenRecord(synced)
      }
    }
    hydrate()
    return () => {
      cancelled = true
    }
  }, [editTokenRecord, supabaseClient, updateEditTokenRecord, user?.id])

  useEffect(() => {
    if (!editAuthPending || !user?.id) {
      clearPollTimer()
      return
    }
    let cancelled = false
    let attempts = 0
    const poll = async () => {
      const synced = await syncGa4EditTokenFromSession(
        supabaseClient,
        user.id
      )
      if (cancelled) return
      if (synced?.token && isGa4EditTokenValid(synced)) {
        updateEditTokenRecord(synced)
        return
      }
      if (attempts >= maxPollAttempts) {
        setEditAuthPending(false)
        setEditAuthError(POLL_FAILURE_MESSAGE)
        clearPollTimer()
        return
      }
      attempts += 1
      timerRef.current = setTimeout(poll, pollIntervalMs)
    }
    poll()
    return () => {
      cancelled = true
      clearPollTimer()
    }
  }, [
    clearPollTimer,
    editAuthPending,
    maxPollAttempts,
    pollIntervalMs,
    supabaseClient,
    updateEditTokenRecord,
    user?.id,
  ])

  const startEditAuthFlow = useCallback(async () => {
    if (isGa4EditTokenValid(editTokenRecord)) return
    if (!user?.id) {
      setEditAuthError(SIGN_IN_REQUIRED_MESSAGE)
      return
    }
    clearPollTimer()
    if (editAuthPending) {
      setEditAuthPending(false)
    }
    setEditAuthError(null)
    setEditAuthMessage(null)
    setEditAuthUrl(null)
    setEditPopupBlocked(false)
    try {
      const result = await requestGa4EditToken(supabaseClient, {
        skipBrowserRedirect: true,
        openPopup: true,
      })
      if (result.error) {
        setEditAuthError(result.error.message || "Failed to start Google OAuth")
        setEditAuthPending(false)
        return
      }
      if (!result.url) {
        setEditAuthError(
          "Google did not return an authorization URL. Please try again."
        )
        setEditAuthPending(false)
        return
      }
      setEditAuthUrl(result.url)
      setEditPopupBlocked(Boolean(result.popupBlocked))
      setEditAuthMessage(
        result.popupBlocked
          ? "Pop-up blocked. Use the link below to open the Google authorization flow."
          : "Complete the Google authorization window to grant GA4 edit access, then click Save again."
      )
      setEditAuthPending(true)
    } catch (err) {
      setEditAuthError(err?.message || "Failed to start Google OAuth")
      setEditAuthPending(false)
    }
  }, [
    clearPollTimer,
    editAuthPending,
    editTokenRecord,
    supabaseClient,
    user?.id,
  ])

  const ensureEditToken = useCallback(async () => {
    if (!user?.id) {
      setEditAuthError(SIGN_IN_REQUIRED_MESSAGE)
      return null
    }
    const stored = getStoredGa4EditToken(user.id)
    if (stored && isGa4EditTokenValid(stored)) {
      updateEditTokenRecord(stored)
      return stored.token
    }
    const loaded = await loadEditToken(supabaseClient, user.id)
    if (loaded && isGa4EditTokenValid(loaded)) {
      updateEditTokenRecord(loaded)
      return loaded.token
    }
    const synced = await syncGa4EditTokenFromSession(supabaseClient, user.id)
    if (synced && isGa4EditTokenValid(synced)) {
      updateEditTokenRecord(synced)
      return synced.token
    }
    await startEditAuthFlow()
    return null
  }, [
    startEditAuthFlow,
    supabaseClient,
    updateEditTokenRecord,
    user?.id,
  ])

  const handleEditAuthFailure = useCallback(
    async (message) => {
      clearStoredGa4EditToken()
      updateEditTokenRecord(null)
      if (user?.id) {
        await saveEditToken(supabaseClient, user.id, {
          access_token: null,
        }).catch(() => {})
      }
      resetAuthorizationState()
      setEditAuthError(message || DENIED_MESSAGE)
    },
    [resetAuthorizationState, supabaseClient, updateEditTokenRecord, user?.id]
  )

  return useMemo(
    () => ({
      editTokenRecord,
      editAuthPending,
      editAuthMessage,
      editAuthError,
      editAuthUrl,
      editPopupBlocked,
      updateEditTokenRecord,
      resetAuthorizationState,
      refreshTokenFromStorage,
      startEditAuthFlow,
      ensureEditToken,
      handleEditAuthFailure,
    }),
    [
      editAuthError,
      editAuthMessage,
      editAuthPending,
      editAuthUrl,
      editPopupBlocked,
      editTokenRecord,
      ensureEditToken,
      handleEditAuthFailure,
      refreshTokenFromStorage,
      resetAuthorizationState,
      startEditAuthFlow,
      updateEditTokenRecord,
    ]
  )
}
